import { ReactNode, useEffect, useMemo } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./layouts/AppShell";
import { OnboardingLandingPage } from "./pages/OnboardingLandingPage";
import { EarlyAccessLandingPage } from "./pages/EarlyAccessLandingPage";
import { HomePage } from "./pages/HomePage";
import { ScansPage } from "./pages/ScansPage";
import { ScanDetailPage } from "./pages/ScanDetailPage";
import { PantryPage } from "./pages/PantryPage";
import { ShoppingListPage } from "./pages/ShoppingListPage";
import { AIInsightsPage } from "./pages/AIInsightsPage";
import { AIInsightDetailPage } from "./pages/AIInsightDetailPage";
import { ExplorePage } from "./pages/ExplorePage";
import { ExploreFoodDetailPage } from "./pages/ExploreFoodDetailPage";
import { MorePage } from "./pages/MorePage";
import { SettingsPage } from "./pages/SettingsPage";
import { DietitianBlogsPage } from "./pages/DietitianBlogsPage";
import { DietitianBlogDetailPage } from "./pages/DietitianBlogDetailPage";
import { DietitianBlogEditorPage } from "./pages/DietitianBlogEditorPage";
import { VisitsPage } from "./pages/VisitsPage";
import { DietitianInsightsReviewPage } from "./pages/DietitianInsightsReviewPage";
import { AdminUserCostsPage } from "./pages/AdminUserCostsPage";
import { SetupRequiredPage } from "./pages/SetupRequiredPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { Card, Skeleton } from "./components/ui";
import { logoutSession } from "./lib/api";
import { setUserProperties, trackEvent, trackPageView } from "./lib/analytics";
import { useSession } from "./hooks/useSession";
import type { SessionState } from "./types";

function DietitianOnlyRoute({
  isDietitian,
  children,
}: {
  isDietitian: boolean;
  children: ReactNode;
}) {
  if (!isDietitian) {
    return <Navigate to="/app/home" replace />;
  }
  return <>{children}</>;
}

function UserOnlyRoute({
  isDietitian,
  children,
}: {
  isDietitian: boolean;
  children: ReactNode;
}) {
  if (isDietitian) {
    return <Navigate to="/app/more" replace />;
  }
  return <>{children}</>;
}

function PublicBlogOutlet({ session }: { session: SessionState }) {
  return <Outlet context={{ session }} />;
}

function anonymousSessionFromBootstrap(bootstrap: Record<string, unknown>): SessionState {
  const prelaunchMode = Boolean(bootstrap.prelaunchMode);
  const prelaunchLaunchAt = typeof bootstrap.prelaunchLaunchAt === "string" ? bootstrap.prelaunchLaunchAt : undefined;
  const loginUrl = typeof bootstrap.loginUrl === "string" ? bootstrap.loginUrl : undefined;
  const userLoginUrl = typeof bootstrap.userLoginUrl === "string" ? bootstrap.userLoginUrl : loginUrl;
  const dietitianLoginUrl = typeof bootstrap.dietitianLoginUrl === "string" ? bootstrap.dietitianLoginUrl : undefined;
  return {
    isAuthenticated: false,
    authMode: "user",
    loginUrl,
    userLoginUrl,
    dietitianLoginUrl,
    prelaunchMode,
    prelaunchLaunchAt,
    user: null,
    consent: { missingStep: null },
    provisioning: {
      isProvisioned: true,
      setupRequired: false,
      reason: null,
      appStoreUrl: typeof bootstrap.appStoreUrl === "string" ? bootstrap.appStoreUrl : undefined,
    },
    policyVersions: {},
  };
}

function AppRoutes() {
  const { session, loading, error, refresh } = useSession();

  const bootstrap = useMemo(() => window.__PANTRYRX_WEBAPP_BOOTSTRAP__ ?? {}, []);
  const isPublicBlogPath = /^\/dietitian-says(?:\/|$)/.test(window.location.pathname);
  const publicSession = session ?? anonymousSessionFromBootstrap(bootstrap as Record<string, unknown>);
  const userLoginUrl = session?.userLoginUrl ?? bootstrap.userLoginUrl ?? bootstrap.loginUrl ?? "/app/auth/sign-in/user/";
  const dietitianLoginUrl =
    session?.dietitianLoginUrl ?? bootstrap.dietitianLoginUrl ?? "/app/auth/sign-in/dietitian/";

  async function handleLogout(): Promise<void> {
    trackEvent("sign_out", { surface: "app_routes" });
    try {
      const payload = await logoutSession();
      window.location.href = payload.redirectUrl || "/";
    } catch {
      window.location.href = "/";
    }
  }

  function startSignIn(
    mode: "user" | "dietitian",
    nextPath = "/app/home",
    extras?: Record<string, string>,
    source?: string,
  ): void {
    trackEvent("auth_start", {
      mode,
      next_path: nextPath,
      source: source || "webapp",
      waitlist_intent: extras?.waitlist === "1",
    });
    const baseUrl = mode === "dietitian" ? dietitianLoginUrl : userLoginUrl;
    const params = new URLSearchParams({ next: nextPath });
    for (const [key, value] of Object.entries(extras ?? {})) {
      params.set(key, value);
    }
    const separator = baseUrl.includes("?") ? "&" : "?";
    const target = `${baseUrl}${separator}${params.toString()}`;
    window.location.href = target;
  }

  const isAuthenticated = Boolean(session?.isAuthenticated);
  const prelaunchMode = Boolean(session?.prelaunchMode ?? bootstrap.prelaunchMode);
  const prelaunchLaunchAt = session?.prelaunchLaunchAt ?? bootstrap.prelaunchLaunchAt;

  const applyAnalyticsUserProperties = () => {
    if (!session?.isAuthenticated || !session.user) {
      setUserProperties({ authenticated: false });
      return;
    }
    setUserProperties({
      authenticated: true,
      role: session.user.isDietitian ? "dietitian" : "member",
      auth_mode: session.authMode || "unknown",
    });
  };

  useEffect(() => {
    applyAnalyticsUserProperties();
  }, [session?.isAuthenticated, session?.authMode, session?.user]);

  useEffect(() => {
    const onEnabled = () => {
      applyAnalyticsUserProperties();
    };
    window.addEventListener("prx:analytics-enabled", onEnabled);
    return () => {
      window.removeEventListener("prx:analytics-enabled", onEnabled);
    };
  }, [session?.isAuthenticated, session?.authMode, session?.user]);

  if (loading && !isPublicBlogPath) {
    return (
      <div className="mx-auto max-w-[860px] space-y-4 px-4 py-8">
        <Skeleton className="h-56" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (error && !session && !isPublicBlogPath) {
    return (
      <div className="mx-auto max-w-[860px] px-4 py-8">
        <Card>
          <h1 className="text-lg font-semibold">Unable to initialize session</h1>
          <p className="mt-2 text-sm text-[var(--pr-text-muted)]">{error}</p>
          <button className="pr-btn-secondary mt-4 px-4 py-2 text-sm font-semibold" onClick={() => void refresh()}>
            Retry
          </button>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route element={<PublicBlogOutlet session={publicSession} />}>
          <Route path="/dietitian-says" element={<DietitianBlogsPage publicView basePath="/dietitian-says" />} />
          <Route path="/dietitian-says/:blogId" element={<DietitianBlogDetailPage publicView basePath="/dietitian-says" />} />
        </Route>
        <Route
          path="*"
          element={
            prelaunchMode ? (
              <EarlyAccessLandingPage
                launchAtIso={prelaunchLaunchAt}
                onJoinWaitlist={() => startSignIn("user", "/", { waitlist: "1" }, "early_access")}
              />
            ) : (
              <OnboardingLandingPage
                isSigningIn={false}
                onSignInAsDietitian={() => startSignIn("dietitian", "/app/home", undefined, "onboarding")}
                onSignInAsUser={() => startSignIn("user", "/app/home", undefined, "onboarding")}
              />
            )
          }
        />
      </Routes>
    );
  }

  const isDietitian = Boolean(session?.user?.isDietitian);
  const setupRequired = Boolean(session?.provisioning?.setupRequired || session?.provisioning?.isProvisioned === false);
  if (setupRequired) {
    const appStoreUrl = session?.provisioning?.appStoreUrl ?? bootstrap.appStoreUrl;
    return (
      <Routes>
        <Route
          path="/app/setup-required"
          element={
            <SetupRequiredPage
              appStoreUrl={appStoreUrl}
              onSignOut={() => {
                void handleLogout();
              }}
            />
          }
        />
        <Route path="*" element={<Navigate to="/app/setup-required" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Routes>
        <Route element={<PublicBlogOutlet session={session!} />}>
          <Route path="/dietitian-says" element={<DietitianBlogsPage publicView basePath="/dietitian-says" />} />
          <Route path="/dietitian-says/:blogId" element={<DietitianBlogDetailPage publicView basePath="/dietitian-says" />} />
        </Route>
        <Route path="/app" element={<AppShell session={session!} onLogout={() => void handleLogout()} />}>
          <Route index element={<Navigate to="/app/home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route
            path="scans"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <ScansPage />
              </UserOnlyRoute>
            }
          />
          <Route
            path="scans/:scanId"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <ScanDetailPage />
              </UserOnlyRoute>
            }
          />
          <Route
            path="pantry"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <PantryPage />
              </UserOnlyRoute>
            }
          />
          <Route
            path="shopping-list"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <ShoppingListPage />
              </UserOnlyRoute>
            }
          />
          <Route
            path="ai-insights"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <AIInsightsPage />
              </UserOnlyRoute>
            }
          />
          <Route
            path="ai-insights/review"
            element={
              <DietitianOnlyRoute isDietitian={isDietitian}>
                <DietitianInsightsReviewPage />
              </DietitianOnlyRoute>
            }
          />
          <Route
            path="ai-insights/:jobId"
            element={
              <UserOnlyRoute isDietitian={isDietitian}>
                <AIInsightDetailPage />
              </UserOnlyRoute>
            }
          />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="explore/:source/:fdcId" element={<ExploreFoodDetailPage />} />
          <Route path="dietitian-says" element={<DietitianBlogsPage />} />
          <Route
            path="dietitian-says/mine"
            element={
              <DietitianOnlyRoute isDietitian={isDietitian}>
                <DietitianBlogsPage mine />
              </DietitianOnlyRoute>
            }
          />
          <Route
            path="dietitian-says/new"
            element={
              <DietitianOnlyRoute isDietitian={isDietitian}>
                <DietitianBlogEditorPage />
              </DietitianOnlyRoute>
            }
          />
          <Route
            path="dietitian-says/:blogId/edit"
            element={
              <DietitianOnlyRoute isDietitian={isDietitian}>
                <DietitianBlogEditorPage />
              </DietitianOnlyRoute>
            }
          />
          <Route path="dietitian-says/:blogId" element={<DietitianBlogDetailPage />} />
          <Route path="visits" element={<VisitsPage />} />
          <Route path="admin/user-costs" element={<AdminUserCostsPage />} />
          <Route path="more" element={<MorePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/app/home" replace />} />
      </Routes>
    </>
  );
}

function RouteAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}`;
    const topLevelRoute = location.pathname.split("/").filter(Boolean)[1] || "root";
    trackPageView(pagePath, { top_level_route: topLevelRoute });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onEnabled = () => {
      const pagePath = `${window.location.pathname}${window.location.search}`;
      const topLevelRoute = window.location.pathname.split("/").filter(Boolean)[1] || "root";
      trackPageView(pagePath, { top_level_route: topLevelRoute, source: "consent_enabled" });
    };
    window.addEventListener("prx:analytics-enabled", onEnabled);
    return () => {
      window.removeEventListener("prx:analytics-enabled", onEnabled);
    };
  }, []);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <RouteAnalyticsTracker />
      <AppRoutes />
    </BrowserRouter>
  );
}
